import React, { useEffect, useState, useCallback } from 'react';

const KNOWN_SETTINGS = [
  { key: 'EXPENSE_VARIANCE_BAND_PCT', label: 'Variance Band (%)', desc: 'Acceptable deviation from rolling median before anomaly flag', value_type: 'NUMBER' },
  { key: 'EXPENSE_ESCALATION_HOURS', label: 'Escalation Hours', desc: 'Hours after which an unactioned submission auto-escalates', value_type: 'NUMBER' },
  { key: 'EXPENSE_DUPLICATE_DAYS', label: 'Duplicate Window (days)', desc: 'Look-back window for hard duplicate detection', value_type: 'NUMBER' },
  { key: 'EXPENSE_ATTACHMENT_MAX_MB', label: 'Max Attachment Size (MB)', desc: 'Per-file upload limit', value_type: 'NUMBER' },
  { key: 'EXPENSE_AUTO_POST_ON_APPROVE', label: 'Auto-Post on Approve', desc: 'If TRUE, approved expenses are immediately posted to journal', value_type: 'BOOLEAN' },
  { key: 'EXPENSE_PETTY_CASH_CAP', label: 'Petty Cash Per-Txn Cap', desc: 'Maximum amount per petty-cash payout', value_type: 'NUMBER' }
];

function PmsExpenseSettings({ token, selectedProperty }) {
  const [settings, setSettings] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [seedStatus, setSeedStatus] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [overwriteSeeded, setOverwriteSeeded] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const loadStatus = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch(`/api/v1/expense/admin/seed/status/${selectedProperty.id}`, { headers: headers() });
      if (res.status === 403) { setSeedStatus(null); return; }
      const data = await res.json();
      if (!res.ok) return;
      setSeedStatus(data);
    } catch { /* ignore — status is optional */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const loadData = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/expense/settings', { headers: headers() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to load');
      const map = {};
      (data.rows || []).forEach(r => { map[r.setting_key] = { value: r.setting_value, value_type: r.value_type, is_seeded: r.is_seeded }; });
      setSettings(map);
      setError('');
    } catch { setError('Network error'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  useEffect(() => { loadData(); loadStatus(); }, [loadData, loadStatus]);

  const setVal = (key, val) => setSettings(s => ({ ...s, [key]: { ...(s[key] || {}), value: val, value_type: (s[key]?.value_type) || KNOWN_SETTINGS.find(k => k.key === key)?.value_type || 'STRING' } }));

  const save = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = { settings: {} };
      for (const k of Object.keys(settings)) {
        payload.settings[k] = { value: settings[k].value, value_type: settings[k].value_type };
      }
      const res = await fetch('/api/v1/expense/settings', {
        method: 'PUT',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Save failed');
      setMessage('Settings saved.');
      loadData();
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  const seedStandardMasters = async () => {
    if (!selectedProperty?.id) return setError('Select a property first');
    const warn = overwriteSeeded
      ? 'OVERWRITE mode: this will reset edited seeded rows back to defaults. Continue?'
      : 'Seed standard categories, approval rules, no-invoice rules, settings, COA expense accounts, and role templates for this property?';
    if (!window.confirm(warn)) return;
    setSeeding(true);
    setError('');
    setMessage('');
    setSeedResult(null);
    try {
      const res = await fetch(`/api/v1/expense/admin/seed/property/${selectedProperty.id}`, {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ overwriteSeeded })
      });
      const data = await res.json();
      if (res.status === 403) return setError('Admin role required to run the seeder.');
      if (!res.ok) {
        // Surface the FULL diagnostic payload — backend may include seederStep,
        // code, where, hint, stack. The plain error message alone hides which
        // step failed.
        setSeedResult(data);
        const bits = [data.error || 'Seeder failed'];
        if (data.seederStep) bits.push(`step: ${data.seederStep}`);
        if (data.code) bits.push(`code: ${data.code}`);
        return setError(bits.join(' · '));
      }
      setSeedResult(data);
      setMessage('Standard masters seeded.');
      loadStatus();
      loadData();
    } catch { setError('Network error'); }
    finally { setSeeding(false); }
  };

  const inputStyle = { padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' };

  const statusTile = (label, n) => (
    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '0.5rem 0.7rem', minWidth: 110 }}>
      <div style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '1.2rem', fontWeight: 700, color: n > 0 ? '#111827' : '#9ca3af' }}>{n}</div>
    </div>
  );

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.8rem' }}>Expense Module Settings</h2>
      {!selectedProperty?.id && <div className="error-banner">Select a property first.</div>}
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{error}</div>}
      {message && <div style={{ background: '#dcfce7', color: '#166534', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{message}</div>}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', marginBottom: '0.8rem' }}>
        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem' }}>Standard Masters Bundle</h3>
        <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0 0 0.7rem 0' }}>
          Bulk-loads ~80 rows defined by SRS §11A: 20 categories (with parent/child hierarchy), 9 no-invoice rules, 6 settings,
          3 approval rule defaults, 12 seasonality factors, 20 COA expense accounts, 6 role templates, 5 AI feature_keys.
          Idempotent — safe to re-run; existing rows you've edited are preserved unless "Overwrite" is enabled.
          Requires admin role.
        </p>

        {seedStatus && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.7rem' }}>
            {statusTile('Categories', seedStatus.categories)}
            {statusTile('Approval Rules', seedStatus.approval_rules)}
            {statusTile('No-Invoice Rules', seedStatus.no_invoice_rules)}
            {statusTile('Seasonality', seedStatus.seasonality)}
            {statusTile('Settings', seedStatus.settings)}
            {statusTile('AI Feature Keys', seedStatus.ai_feature_keys)}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
            <input type="checkbox" checked={overwriteSeeded} onChange={e => setOverwriteSeeded(e.target.checked)} />
            <span>Overwrite edited seeded rows (reset to defaults)</span>
          </label>
          <button onClick={seedStandardMasters} disabled={seeding || !selectedProperty?.id}
            style={{ padding: '0.55rem 1.2rem', border: 'none', borderRadius: 6, background: '#8b5cf6', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
            {seeding ? 'Seeding…' : 'Seed Standard Masters'}
          </button>
        </div>

        {seedResult && (
          <pre style={{ marginTop: '0.7rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '0.6rem', fontSize: '0.75rem', maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify(seedResult, null, 2)}
          </pre>
        )}
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem' }}>
        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem' }}>Per-Property Settings</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
          {KNOWN_SETTINGS.map(s => {
            const cur = settings[s.key]?.value ?? '';
            return (
              <div key={s.key} style={{ border: '1px solid #f3f4f6', borderRadius: 6, padding: '0.7rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>{s.label}</div>
                <div style={{ color: '#6b7280', fontSize: '0.78rem', marginBottom: '0.5rem' }}>{s.desc}</div>
                {s.value_type === 'BOOLEAN' ? (
                  <select value={String(cur)} onChange={e => setVal(s.key, e.target.value)} style={inputStyle}>
                    <option value="">— Not set —</option>
                    <option value="true">Enabled</option>
                    <option value="false">Disabled</option>
                  </select>
                ) : s.value_type === 'NUMBER' ? (
                  <input type="number" step="0.01" value={cur} onChange={e => setVal(s.key, e.target.value)} style={inputStyle} />
                ) : (
                  <input value={cur} onChange={e => setVal(s.key, e.target.value)} style={inputStyle} />
                )}
                <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: '#9ca3af' }}>Key: <code>{s.key}</code></div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button onClick={save} disabled={saving} style={{ padding: '0.5rem 1.2rem', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>{saving ? 'Saving…' : 'Save Settings'}</button>
        </div>
      </div>
    </div>
  );
}

export default PmsExpenseSettings;
